/**
 * Streak Hooks — React Query hooks for user-facing streak operations.
 *
 * Covers: list my streaks, get single streak, pause/unpause, purchase freeze,
 * and the badges earned from streak milestones.
 * The StreaksApi client is auto-generated from backend swagger specs.
 */

import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {
    StreaksApi,
    Token,
    MyStreaksResponse,
    MyStreakEntry,
    StreakStatus,
    GetStreaksMeIncludeAvailableEnum,
} from "@devour/client";
import getConfig from "@/utils/getConfig";

const STREAKS_KEY = "my-streaks";
const STREAK_KEY = "my-streak";
const STREAK_BADGES_KEY = "my-streak-badges";

/**
 * Badges earned from streak milestones.
 *
 * Deliberately a separate query from the streaks themselves: a badge is
 * permanent and survives the streak that earned it, so its cache should not be
 * invalidated every time a streak's window progress ticks. It changes only when
 * a milestone fires.
 */
export function useMyStreakBadges(fullToken: Token | undefined, streakConfigId?: string) {
    return useQuery({
        queryKey: [STREAK_BADGES_KEY, fullToken?.id, streakConfigId],
        queryFn: () => new StreaksApi(getConfig(fullToken)).getStreaksMeBadges({streakConfigId}),
        // Badges only change at a milestone; no reason to refetch on window ticks.
        staleTime: 5 * 60 * 1000,
        enabled: Boolean(fullToken),
    });
}

export function useMyStreaks(fullToken: Token | undefined, includeAvailable = true) {
    /*
     * Defensive fallback: compiled `@devour/client` enums have intermittently
     * evaluated to undefined at runtime (Vite HMR / chunk-load race), and a
     * raw `EnumName.True` access throws "Cannot read properties of undefined
     * (reading 'True')" — same Sentry pattern guarded in useGameSearchInfinite.ts.
     * Wire literals match the enum's own values, so behavior is identical.
     */
    const includeAvailableValue = (includeAvailable
        ? GetStreaksMeIncludeAvailableEnum?.True ?? "true"
        : GetStreaksMeIncludeAvailableEnum?.False ?? "false") as GetStreaksMeIncludeAvailableEnum;
    return useQuery({
        queryKey: [STREAKS_KEY, fullToken?.id, includeAvailable],
        queryFn: () => new StreaksApi(getConfig(fullToken)).getStreaksMe({
            includeAvailable: includeAvailableValue,
        }),
        /*
         * 60s stale time — streak window progress updates continuously, but exact
         * second-level freshness is not required for UI; urgency at <2h is driven
         * by the STREAK_WARNING WebSocket event, not by polling this query.
         */
        staleTime: 60000,
        enabled: Boolean(fullToken),
    });
}

export function useMyStreak(fullToken: Token | undefined, streakConfigId: string) {
    return useQuery({
        queryKey: [STREAK_KEY, fullToken?.id, streakConfigId],
        queryFn: () => new StreaksApi(getConfig(fullToken)).getStreaksMeStreakConfigId({streakConfigId}),
        staleTime: 60000, // See comment on useMyStreaks staleTime
        enabled: Boolean(fullToken) && Boolean(streakConfigId),
    });
}

export function usePauseStreak(fullToken: Token | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (streakConfigId: string) =>
            new StreaksApi(getConfig(fullToken)).createStreaksMeStreakConfigIdPause({streakConfigId, body: {}}),
        onSuccess: (_data, streakConfigId) => {
            queryClient.invalidateQueries({queryKey: [STREAKS_KEY]});
            queryClient.invalidateQueries({queryKey: [STREAK_KEY, fullToken?.id, streakConfigId]});
        },
    });
}

export function useUnpauseStreak(fullToken: Token | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (streakConfigId: string) =>
            new StreaksApi(getConfig(fullToken)).createStreaksMeStreakConfigIdUnpause({streakConfigId, body: {}}),
        onSuccess: (_data, streakConfigId) => {
            queryClient.invalidateQueries({queryKey: [STREAKS_KEY]});
            queryClient.invalidateQueries({queryKey: [STREAK_KEY, fullToken?.id, streakConfigId]});
        },
    });
}

export function usePurchaseFreeze(fullToken: Token | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (streakConfigId: string) =>
            new StreaksApi(getConfig(fullToken)).createStreaksMeStreakConfigIdFreezePurchase({streakConfigId}),
        onSuccess: (_data, streakConfigId) => {
            queryClient.invalidateQueries({queryKey: [STREAKS_KEY]});
            queryClient.invalidateQueries({queryKey: [STREAK_KEY, fullToken?.id, streakConfigId]});
        },
    });
}

/**
 * Derived helpers — compute display data from raw API responses.
 * Keep in hooks file so components stay thin.
 */

export function getTimeRemaining(windowEnd?: number): { hours: number; minutes: number; expired: boolean } {
    if (!windowEnd) return {hours: 0, minutes: 0, expired: true};
    const ms = windowEnd - Date.now();
    if (ms <= 0) return {hours: 0, minutes: 0, expired: true};
    return {
        hours: Math.floor(ms / 3600000),
        minutes: Math.floor(ms % 3600000 / 60000),
        expired: false,
    };
}

export function getNextMilestone(entry: MyStreakEntry): { streakCount: number } | null {
    const currentStreak = entry.streak?.currentStreak ?? 0;
    const claimed = new Set(entry.streak?.claimedMilestones ?? []);
    const milestones = entry.config?.milestones ?? [];

    // Find next milestone occurrence, handling repeating milestones correctly
    const upcomingCounts: number[] = [];
    for (const m of milestones) {
        if (m.isRepeating && m.repeatInterval) {
            // Compute next occurrence at or after currentStreak + 1
            if (currentStreak < m.streakCount) {
                upcomingCounts.push(m.streakCount);
            } else {
                const offset = currentStreak - m.streakCount;
                const cyclesDone = Math.floor(offset / m.repeatInterval) + 1;
                upcomingCounts.push(m.streakCount + cyclesDone * m.repeatInterval);
            }
        } else if (m.streakCount > currentStreak && !claimed.has(m.streakCount)) {
            upcomingCounts.push(m.streakCount);
        }
    }

    upcomingCounts.sort((a, b) => a - b);
    return upcomingCounts.length > 0
        ? {streakCount: upcomingCounts[0]}
        : null;
}

export function getStreakStatusColor(status?: StreakStatus): string {
    switch (status) {
        case StreakStatus.ACTIVE: return "#28a745";
        case StreakStatus.GRACE: return "#ffc107";
        case StreakStatus.FROZEN: return "#17a2b8";
        case StreakStatus.PAUSED: return "#6c757d";
        case StreakStatus.BROKEN: return "#dc3545";
        case StreakStatus.COMPLETED: return "#5E5E5E";
        default: return "#6c757d";
    }
}
